// schema.org/Event structured data for a show page — one Event per
// performance date (rich results: dates, venue, price). Shared by the apps'
// show heads; what differs per site comes from SITE: the address parts
// (SITE.eventAddress) and the country. Moved from riviera's event-head.tsx,
// which renders exactly what it rendered before.

import type { ShowDate } from '../event-show';
import { localizedDescription, localizedTitle } from '../i18n/event-content';
import type { Locale } from '../i18n/locales';
import { richResultImage, truncate } from '../seo';
import type { SiteConfig } from '../site-config';
import type { DbEvent } from '../supabase';
import { SITE } from '@site';

// Widened to the SiteConfig shape: eventAddress is optional.
const SITE_CONFIG: SiteConfig = SITE;

/**
 * How many dates get their own Event in the JSON-LD. Google wants one Event
 * per performance of a multi-date show, but a weekly comedy night has 50+;
 * the soonest ones are what a search result can usefully show.
 */
export const MAX_JSON_LD_DATES = 20;

function addressCountry(cityId: string | null): string {
  return (cityId && SITE_CONFIG.eventAddress?.countryByCity?.[cityId]) || SITE_CONFIG.country;
}

/**
 * `location.address` as a schema.org/PostalAddress.
 *
 * Google's Event rich-result validator wants a structured address. We fill
 * only what we know for certain: `addressLocality` is the resolved city's
 * display name (the raw feed address only when `city_id` doesn't resolve),
 * `addressRegion` the site's region for a city in its own country, and
 * `addressCountry` from `city_id`. With `streetAddress` on (Platinumlist,
 * whose venue address is a real one), the feed's address is the street line.
 * Riviera's feed carries a bare uppercase locality ("CANNES"), so it never
 * becomes a street; postalCode stays out rather than guessed.
 */
function postalAddress(event: DbEvent, cityName: string | null) {
  const opts = SITE_CONFIG.eventAddress;
  const street = opts?.streetAddress ? event.address?.trim() : undefined;
  const locality = cityName || (street ? undefined : event.address?.trim());
  const country = addressCountry(event.city_id);
  return {
    '@type': 'PostalAddress',
    ...(street ? { streetAddress: street } : {}),
    ...(locality ? { addressLocality: locality } : {}),
    ...(opts?.region && country === SITE_CONFIG.country ? { addressRegion: opts.region } : {}),
    addressCountry: country,
  };
}

// One Event for one date. `date` supplies the per-date facts (time, venue,
// price, ticket link, sold out); the show's row supplies the name, copy and
// image.
export function eventJsonLd(event: DbEvent, date: ShowDate, cityName: string | null, url: string, locale: Locale) {
  const description = localizedDescription(event, locale);
  // Google lists `endDate` as recommended and warns when it's absent. The
  // AWIN/Fnac feed never supplies one, so a single-instance event is declared
  // as starting and ending at its start time rather than leaving the field off.
  const endDate = date.end_ts ?? date.start_ts;
  const venue = date.venue_name ?? event.venue_name;
  // Feed thumbnails below Google's 696px minimum earn a validation warning and
  // no image in the result either way — richResultImage() drops them, and a
  // placeholder that never loads (usableImageUrl).
  const image = richResultImage(event.image_url);
  const geo =
    SITE_CONFIG.eventAddress?.geo && event.venue_lat != null && event.venue_lng != null
      ? { geo: { '@type': 'GeoCoordinates', latitude: event.venue_lat, longitude: event.venue_lng } }
      : {};
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: localizedTitle(event, locale),
    url,
    startDate: date.start_ts,
    endDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(image ? { image: [image] } : {}),
    ...(description ? { description: truncate(description, 500) } : {}),
    location: {
      '@type': 'Place',
      name: venue ?? cityName ?? SITE.regionName[SITE.defaultLocale as keyof typeof SITE.regionName],
      address: postalAddress(event, cityName),
      ...geo,
    },
    // Headline act from the feed. PerformingGroup is the safe type when we
    // can't tell a person from a band (Fnac's `primary_artist` is either).
    ...(event.performer ? { performer: { '@type': 'PerformingGroup', name: event.performer } } : {}),
    // Approximation: no feed we ingest names an organizer, and the venue is
    // the closest real entity we hold. Omitted rather than guessed when the
    // row has no venue. See docs/seo-structured-data.md.
    ...(venue ? { organizer: { '@type': 'Organization', name: venue } } : {}),
    ...(date.min_price != null
      ? {
          offers: {
            '@type': 'Offer',
            price: date.min_price,
            priceCurrency: date.currency ?? SITE.currency,
            availability: date.sold_out ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
            // Google wants the on-sale date; the feeds don't carry one, and
            // `start_ts` is the only stable date we hold (`fetched_at` is
            // rewritten every sync and would churn the static HTML).
            validFrom: date.start_ts,
            ...(date.ticket_url ? { url: date.ticket_url } : {}),
          },
        }
      : {}),
  };
}
